import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Calendar,
  CheckCircle,
  Clock,
  Star,
  Loader2,
  MessageSquare,
  Award
} from 'lucide-react';
import Button from '@components/common/Button';
import mentorService from '@services/mentor.service';
import { ROUTES } from '@constants';
import toast from 'react-hot-toast';

const QuestionDetail = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    fetchQuestionDetail();
  }, [questionId]);

  const fetchQuestionDetail = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getStudentQuestionById(questionId);
      
      
      
      if (response.success || response.data) {
        // console.log("*********************", response?.data?.data)
        const questionData = response.data?.data?.question || response.data;
        
        
        setQuestion(questionData);
        if (questionData?.rating) {
          setRating(questionData.rating);
        }
      } else {
        toast.error('Failed to load question details');
        navigate(ROUTES.STUDENT_MY_QUESTIONS);
      }
    } catch (error) {
      console.error('Failed to fetch question details:', error);
      console.error('Error details:', error.response); // Debug log
      toast.error('Failed to load question details');
      navigate(ROUTES.STUDENT_MY_QUESTIONS);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingSubmit = async (selectedRating) => {
    if (question.rating) {
      toast.error('You have already rated this answer');
      return;
    }

    try {
      setSubmittingRating(true);
      const response = await mentorService.rateAnswer(questionId, selectedRating);
      
      if (response.success) {
        toast.success('Rating submitted successfully!');
        setRating(selectedRating);
        setQuestion({ ...question, rating: selectedRating });
      }
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMentorName = () => {
    if (!question?.mentor) return 'Mentor';
    const { firstName, lastName } = question.mentor;
    return `${firstName || ''} ${lastName || ''}`.trim() || 'Mentor';
  };

  const getMentorInitial = () => {
    const name = getMentorName();
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-primary-700 mb-2">Question not found</h3>
          <Button onClick={() => navigate(ROUTES.STUDENT_MY_QUESTIONS)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const isAnswered = Boolean(question.answer) && (question.status === 'answered' || question.status === 'approved');

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full bg-white border-b border-gray-200 px-6 py-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(ROUTES.STUDENT_MY_QUESTIONS)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to My Questions</span>
        </button>
      </div>

      {/* Question Card */}
      <div className="max-w-8xl mx-auto p-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between gap-4 mb-4">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                isAnswered
                  ? 'bg-green-100 text-green-700'
                  : 'bg-orange-100 text-orange-700'
              }`}>
                {isAnswered ? (
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" />
                    Answered
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    Pending
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                {formatDate(question.questionDate)}
              </div>
            </div>

            <h1 className="text-2xl font-bold text-primary-700 mb-4">
              Your Question
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed">
              {question.question}
            </p>
          </div>

          {/* Mentor Info */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {getMentorInitial()}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-primary-700">
                  {getMentorName()}
                </h3>
                <p className="text-sm text-gray-600">
                  {question.mentor?.currentPosition || 'Expert Mentor'}
                </p>
                {question.mentor?.averageRating && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= Math.round(question.mentor.averageRating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {parseFloat(question.mentor.averageRating).toFixed(1)} ({question.mentor.totalRatings} ratings)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Answer Section */}
          <div className="p-6">
            {isAnswered ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h2 className="text-xl font-bold text-primary-700">Answer</h2>
                  <span className="text-sm text-gray-500">
                    • {formatDate(question.answerDate)}
                  </span>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {question.answer}
                  </p>
                </div>

                {/* Rating Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-primary-700 mb-4">
                    {question.rating ? 'Your Rating' : 'Rate this Answer'}
                  </h3>
                  
                  {question.rating ? (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-8 h-8 ${
                              star <= question.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <Award className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          You rated this answer {question.rating} star{question.rating !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-4">
                        How helpful was this answer? Your feedback helps improve mentor quality.
                      </p>
                      <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => handleRatingSubmit(star)}
                            onMouseEnter={() => setHoveredRating(star)}
                            onMouseLeave={() => setHoveredRating(0)}
                            disabled={submittingRating}
                            className="transition-transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Star
                              className={`w-10 h-10 ${
                                star <= (hoveredRating || rating)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300 hover:text-yellow-200'
                              }`}
                            />
                          </button>
                        ))}
                        {submittingRating && (
                          <Loader2 className="w-5 h-5 text-secondary-500 animate-spin ml-2" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Click on a star to rate (1 = Poor, 5 = Excellent)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-16 h-16 text-orange-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-primary-700 mb-2">
                  Waiting for Answer
                </h3>
                <p className="text-gray-600">
                  Your mentor will answer your question soon. You'll receive a notification once they respond.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetail;
